import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { api } from '../lib/api';
import { useCart } from '../context/CartContext';
import { toast } from 'sonner';
import { ChevronLeft, Minus, Plus, Package, Ruler, Droplets, Shield, Target, Weight, ScrollText, Hammer, ChevronDown, X, ZoomIn, Clock, AlertTriangle } from 'lucide-react';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import ProductCard from '../components/ProductCard';
import { STORE_CONFIG } from '../config/storeConfig';

const ProductDetailPage = () => {
  const { id } = useParams();
  const { addItem, clearAndAdd, getCartType } = useCart();
  const [product, setProduct] = useState(null);
  const [categories, setCategories] = useState([]);
  const [relatedProducts, setRelatedProducts] = useState([]);
  const [addonProduct, setAddonProduct] = useState(null);
  const [includeAddon, setIncludeAddon] = useState(false);
  const [loading, setLoading] = useState(true);
  const [selectedImage, setSelectedImage] = useState(0);
  const [quantity, setQuantity] = useState(1);
  const [colorPreference, setColorPreference] = useState('');
  const [selectedMagnetOption, setSelectedMagnetOption] = useState(null);
  const [showAddonImage, setShowAddonImage] = useState(false);
  const [showProductImageModal, setShowProductImageModal] = useState(false);
  
  // BTO Checkout State
  const [showBTOModal, setShowBTOModal] = useState(false);
  const [btoQuantity, setBtoQuantity] = useState(1);
  const [btoIncludeAddon, setBtoIncludeAddon] = useState(false);

  // Desktop Skull Options State
  const [desktopSkullControl, setDesktopSkullControl] = useState(''); // 'remote' or 'app'
  const [desktopSkullCovering, setDesktopSkullCovering] = useState(''); // 'black' or 'newspaper'
  // BTO Desktop Skull Options
  const [btoDesktopSkullControl, setBtoDesktopSkullControl] = useState('');
  const [btoDesktopSkullCovering, setBtoDesktopSkullCovering] = useState('');

  // Addon product titles (used to find add-ons dynamically)
  const TRAILER_HITCH_PIN_TITLE = 'trailer hitch pin';
  const SQUIRREL_ADDON_TITLE = 'add a squirrel';
  const NECROBRICK_ADDON_TITLE = 'necro-brick';
  const DESKTOP_SKULL_TITLE = 'desktop skull';

  // Magnet quantity options with shipping details
  const magnetOptions = [
    { qty: 3, price: 6, label: '3 Skull Magnets - $6', ship_weight: 0.125, ship_length: 6, ship_width: 4, ship_height: 2 },
    { qty: 5, price: 10, label: '5 Skull Magnets - $10', ship_weight: 0.3125, ship_length: 6, ship_width: 4, ship_height: 2 },
    { qty: 10, price: 20, label: '10 Skull Magnets - $20', ship_weight: 0.34375, ship_length: 6, ship_width: 4, ship_height: 2 },
    { qty: 20, price: 40, label: '20 Skull Magnets - $40', ship_weight: 0.65625, ship_length: 8, ship_width: 6, ship_height: 3 },
    { qty: 100, price: 150, label: '100 Skull Magnets - $150', ship_weight: 3.6, ship_length: 9, ship_width: 9, ship_height: 2 },
  ];

  const isMiniMagnets = product?.title?.toLowerCase().includes('mini') && product?.title?.toLowerCase().includes('magnet');
  const isSolarSkulls = product?.title?.toLowerCase().includes('solar skull');
  const isTrailerHitchSkullz = product?.title?.toLowerCase().includes('trailer hitch skullz');
  const isDesktopSkull = product?.title?.toLowerCase().includes('desktop skull');
  const isComingSoon = product?.status === 'coming_soon';
  const isAvailableAmazon = product?.status === 'available_amazon';

  // Scroll to top when product page loads or product changes
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [id]);

  useEffect(() => {
    const fetchProduct = async () => {
      try {
        const [productData, categoriesData, allProductsData] = await Promise.all([
          api.getProduct(id),
          api.getCategories(),
          api.getProducts({ include_hidden: true })  // Include hidden products to find add-ons
        ]);
        setProduct(productData);
        setCategories(categoriesData);
        
        // Fetch related products from same category (excluding hidden)
        const relatedFromCategory = allProductsData.filter(p => 
          p.category_id === productData.category_id && 
          p.id !== id && 
          !p.is_hidden
        );
        setRelatedProducts(relatedFromCategory.slice(0, 3));

        // Set default magnet option if it's mini magnets
        if (productData?.title?.includes('MINI SKULL MAGNETS')) {
          setSelectedMagnetOption(magnetOptions[0]);
        }

        // Find addon product by TITLE (not hardcoded ID)
        let addonProduct = null;
        let addonDescription = '';
        
        if (productData?.title?.toLowerCase().includes('trailer hitch skullz')) {
          // Find Trailer Hitch Pin by title
          addonProduct = allProductsData.find(p => p.title?.toLowerCase().trim().includes(TRAILER_HITCH_PIN_TITLE));
          addonDescription = 'Recommended accessory for secure mounting';
        } else if (productData?.title?.toLowerCase().includes(DESKTOP_SKULL_TITLE)) {
          // Find Necro-Brick USB adapter by title for Desktop Skull
          addonProduct = allProductsData.find(p => p.title?.toLowerCase().trim().includes(NECROBRICK_ADDON_TITLE));
          addonDescription = 'Power your skull in style - slim profile hides behind furniture';
        } else if (productData?.category_id) {
          // Check if it's Metal Art category by looking for metal art products
          const isMetalArt = categoriesData.find(c => c.id === productData.category_id)?.slug === 'metal-art';
          if (isMetalArt) {
            // Find squirrel addon by title
            addonProduct = allProductsData.find(p => p.title?.toLowerCase().trim().includes(SQUIRREL_ADDON_TITLE));
            addonDescription = 'Add a fun squirrel companion to your metal art';
          }
        }
        
        if (addonProduct) {
          addonProduct.addonDescription = addonDescription;
          setAddonProduct(addonProduct);
        } else {
          setAddonProduct(null);
        }
        setIncludeAddon(false);
      } catch (error) {
        console.error('Error fetching product:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchProduct();
    setColorPreference('');
  }, [id]);

  const handleAddToCart = () => {
    // Validate Desktop Skull options
    if (isDesktopSkull) {
      if (!desktopSkullControl) {
        toast.error('Please select a control type (With Remote or App Controlled)');
        return;
      }
      if (!desktopSkullCovering) {
        toast.error('Please select a base covering (Black or Vintage Newspaper)');
        return;
      }
    }

    if (isMiniMagnets && selectedMagnetOption) {
      const magnetProduct = {
        ...product,
        price: selectedMagnetOption.price,
        title: `${product.title} (${selectedMagnetOption.qty} pcs)`,
        magnetQty: selectedMagnetOption.qty,
        // Override shipping dimensions based on selected quantity
        ship_weight: selectedMagnetOption.ship_weight,
        ship_length: selectedMagnetOption.ship_length,
        ship_width: selectedMagnetOption.ship_width,
        ship_height: selectedMagnetOption.ship_height
      };
      const result = addItem(magnetProduct);
      if (result.blocked) {
        toast.error(
          `Your cart contains marketplace items. Please checkout or clear your cart first.`,
          {
            duration: 5000,
            action: {
              label: 'Clear Cart',
              onClick: () => {
                clearAndAdd(magnetProduct);
                toast.success(`Cart cleared. ${selectedMagnetOption.qty} Skull Magnets added!`);
              }
            }
          }
        );
        return;
      }
      toast.success(`${selectedMagnetOption.qty} Skull Magnets added to cart`);
    } else {
      // Build product with options
      let productWithOptions = { ...product };
      
      if (colorPreference) {
        productWithOptions.colorPreference = colorPreference;
      }
      
      // Add Desktop Skull options
      if (isDesktopSkull) {
        productWithOptions.desktopSkullControl = desktopSkullControl;
        productWithOptions.desktopSkullCovering = desktopSkullCovering;
        productWithOptions.specialNotes = `Control: ${desktopSkullControl === 'remote' ? 'With Remote' : 'App Controlled'} | Base: ${desktopSkullCovering === 'black' ? 'Black Covering' : 'Vintage Newspaper'}`;
      }
      
      // Check first item for blocked status
      const result = addItem(productWithOptions);
      if (result.blocked) {
        toast.error(
          `Your cart contains marketplace items. Please checkout or clear your cart first.`,
          {
            duration: 5000,
            action: {
              label: 'Clear Cart',
              onClick: () => {
                clearAndAdd(productWithOptions);
                toast.success(`Cart cleared. ${product.title} added!`);
              }
            }
          }
        );
        return;
      }
      
      // Add remaining quantity
      for (let i = 1; i < quantity; i++) {
        addItem(productWithOptions);
      }
      
      // Add addon product if selected
      if (includeAddon && addonProduct) {
        addItem(addonProduct);
        toast.success(`${quantity} x ${product.title} + ${addonProduct.title.trim()} added to cart`);
      } else {
        toast.success(`${quantity} x ${product.title} added to cart`);
      }
      setColorPreference('');
      // Reset Desktop Skull options
      if (isDesktopSkull) {
        setDesktopSkullControl('');
        setDesktopSkullCovering('');
      }
    }
  };

  // BTO Functions
  const handleOpenBTOModal = () => {
    setBtoQuantity(1);
    setBtoIncludeAddon(false);
    setBtoDesktopSkullControl('');
    setBtoDesktopSkullCovering('');
    setShowBTOModal(true);
  };

  const getBTOSubtotal = () => {
    let subtotal = product.price * btoQuantity;
    if (btoIncludeAddon && addonProduct) {
      subtotal += addonProduct.price;
    }
    return subtotal;
  };

  const handleAddBTOToCart = () => {
    // Validate Desktop Skull options for BTO
    if (isDesktopSkull) {
      if (!btoDesktopSkullControl) {
        toast.error('Please select a control type (With Remote or App Controlled)');
        return;
      }
      if (!btoDesktopSkullCovering) {
        toast.error('Please select a base covering (Black or Vintage Newspaper)');
        return;
      }
    }

    // Build special notes
    let specialNotes = '[BTO - BUILT TO ORDER]';
    if (isDesktopSkull) {
      specialNotes += `\nControl: ${btoDesktopSkullControl === 'remote' ? 'With Remote' : 'App Controlled'}`;
      specialNotes += `\nBase: ${btoDesktopSkullCovering === 'black' ? 'Black Covering' : 'Vintage Newspaper'}`;
    }

    // Create BTO product with unique ID
    const btoProduct = {
      ...product,
      id: `${product.id}-bto-${Date.now()}`, // Unique ID for each BTO order
      originalProductId: product.id,
      title: `[BTO] ${product.title}`,
      btoOrder: true,
      specialNotes: specialNotes,
      ...(isDesktopSkull && {
        desktopSkullControl: btoDesktopSkullControl,
        desktopSkullCovering: btoDesktopSkullCovering
      })
    };
    
    // Add main product
    const result = addItem(btoProduct);
    if (result.blocked) {
      toast.error(
        `Your cart contains marketplace items. Please checkout or clear your cart first.`,
        {
          duration: 5000,
          action: {
            label: 'Clear Cart',
            onClick: () => {
              clearAndAdd(btoProduct);
              // Add remaining quantity
              for (let i = 1; i < btoQuantity; i++) {
                addItem(btoProduct);
              }
              // Add addon if selected
              if (btoIncludeAddon && addonProduct) {
                addItem({ ...addonProduct, id: `${addonProduct.id}-bto-addon-${Date.now()}`, btoAddon: true });
              }
              toast.success('Cart cleared. BTO item added!');
            }
          }
        }
      );
      return;
    }
    
    // Add remaining quantity (same product, will increment)
    for (let i = 1; i < btoQuantity; i++) {
      addItem(btoProduct);
    }
    
    // Add addon if selected
    if (btoIncludeAddon && addonProduct) {
      addItem({ ...addonProduct, id: `${addonProduct.id}-bto-addon-${Date.now()}`, btoAddon: true });
      toast.success(`BTO: ${btoQuantity} x ${product.title} + ${addonProduct.title.trim()} added to cart`);
    } else {
      toast.success(`BTO: ${btoQuantity} x ${product.title} added to cart`);
    }
    
    setShowBTOModal(false);
  };

  if (loading) {
    return (
      <div className="pt-20 min-h-screen bg-void flex items-center justify-center">
        <div className="w-16 h-16 border-4 border-cyan-400/20 border-t-cyan-400 rounded-full animate-spin" />
      </div>
    );
  }

  if (!product) {
    return (
      <div className="pt-20 min-h-screen bg-void flex flex-col items-center justify-center">
        <p className="text-neutral-400 mb-4">Product not found</p>
        <Link to="/shop" className="text-cyan-400 hover:underline">
          Back to Shop
        </Link>
      </div>
    );
  }

  return (
    <div data-testid="product-detail-page" className="pt-20 min-h-screen bg-void">
      {/* Breadcrumb */}
      <div className="px-6 md:px-12 lg:px-24 py-6 border-b border-white/5">
        <Link
          to="/shop"
          data-testid="back-to-shop"
          className="inline-flex items-center gap-2 text-neutral-400 hover:text-white font-mono text-sm uppercase tracking-widest transition-colors"
        >
          <ChevronLeft className="w-4 h-4" /> Back to Shop
        </Link>
      </div>

      {/* Product Content */}
      <div className="px-6 md:px-12 lg:px-24 py-12">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-24">
            {/* Image Gallery */}
            <div className="lg:sticky lg:top-28 lg:h-fit">
              {/* Main Image */}
              <div 
                className="aspect-square bg-void-subtle overflow-hidden mb-4 relative cursor-pointer group"
                onClick={() => setShowProductImageModal(true)}
              >
                <img
                  src={product.images[selectedImage]?.url}
                  alt={product.images[selectedImage]?.alt || product.title}
                  data-testid="main-product-image"
                  className={`w-full h-full object-cover transition-transform duration-300 group-hover:scale-105 ${isComingSoon ? 'opacity-95' : ''}`}
                />
                
                {/* Coming Soon Banner */}
                {isComingSoon && (
                  <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-black/20" />
                    <div className="relative z-10 bg-amber-500 text-black px-8 py-3 transform -rotate-12 shadow-2xl">
                      <span className="font-mono text-lg font-bold uppercase tracking-widest flex items-center gap-2">
                        <Clock className="w-5 h-5" />
                        Coming Soon!
                      </span>
                    </div>
                  </div>
                )}
                
                {!isComingSoon && (
                  <div className="absolute inset-0 bg-black/30 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center pointer-events-none">
                    <div className="bg-black/60 rounded-full p-3">
                      <ZoomIn className="w-8 h-8 text-white" />
                    </div>
                  </div>
                )}
              </div>
              {/* Thumbnails */}
              {product.images.length > 1 && (
                <div className="flex gap-4">
                  {product.images.map((image, index) => (
                    <button
                      key={image.url || `thumb-${index}`}
                      data-testid={`thumbnail-${index}`}
                      onClick={() => setSelectedImage(index)}
                      className={`w-20 h-20 overflow-hidden border-2 transition-colors ${
                        selectedImage === index
                          ? 'border-cyan-400'
                          : 'border-transparent hover:border-white/20'
                      }`}
                    >
                      <img
                        src={image.url}
                        alt={image.alt}
                        className="w-full h-full object-cover"
                      />
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Product Info */}
            <div>
              {/* Coming Soon Badge */}
              {isComingSoon && (
                <div className="bg-amber-500/20 border border-amber-500/50 px-4 py-2 mb-4 inline-flex items-center gap-2">
                  <Clock className="w-4 h-4 text-amber-500" />
                  <span className="font-mono text-sm uppercase tracking-widest text-amber-500">
                    In Testing - Awaiting Release
                  </span>
                </div>
              )}
              <span className="text-xs font-mono uppercase tracking-widest text-cyan-400 mb-2 block">
                {categories.find(c => c.id === product.category_id)?.name || product.category_id}
              </span>
              <h1
                data-testid="product-title"
                className="font-heading text-3xl md:text-5xl font-bold tracking-tight text-white mb-4"
              >
                {product.title}
              </h1>
              {isMiniMagnets ? (
                <p
                  data-testid="product-price"
                  className="font-mono text-3xl text-cyan-400 mb-2"
                >
                  Starting at $6.00
                </p>
              ) : (
                <p
                  data-testid="product-price"
                  className="font-mono text-3xl text-cyan-400 mb-2"
                >
                  ${product.price.toFixed(2)}
                </p>
              )}

              {/* Shipping Info */}
              <p className="text-sm text-neutral-400 mb-6 flex items-center gap-2">
                <Package className="w-4 h-4" />
                Shipping calculated at checkout
              </p>

              {/* Stock Status - Hide for Coming Soon products */}
              {!isComingSoon && !isMiniMagnets && product.stock <= 3 && (
                <div className="mb-6">
                  <p className="text-sm text-cyan-400 font-mono uppercase tracking-widest">
                    {product.stock === 0
                      ? '0 left in stock'
                      : product.stock === 1
                      ? 'Only 1 left in stock!'
                      : `Only ${product.stock} left in stock!`}
                  </p>
                  {product.stock === 0 && (
                    <div className="mt-2 flex items-center gap-2 text-amber-400 animate-pulse">
                      <ChevronDown className="w-4 h-4" />
                      <ChevronDown className="w-4 h-4" />
                      <span className="text-sm font-mono uppercase tracking-widest">BTO Order Available</span>
                      <ChevronDown className="w-4 h-4" />
                      <ChevronDown className="w-4 h-4" />
                    </div>
                  )}
                </div>
              )}

              <p className="text-neutral-300 leading-relaxed mb-8">
                {product.description}
              </p>

              {/* Coming Soon Message */}
              {isComingSoon && (
                <div className="mb-8 p-6 bg-amber-500/10 border border-amber-500/30">
                  <div className="flex items-center gap-3 mb-3">
                    <Clock className="w-6 h-6 text-amber-500" />
                    <h3 className="font-mono text-lg font-bold uppercase tracking-widest text-amber-500">
                      Coming Soon!
                    </h3>
                  </div>
                  <p className="text-neutral-300 text-sm">
                    This product is currently in testing and will be available soon. 
                    Check back later or follow us on social media for release updates!
                  </p>
                </div>
              )}

              {/* Availability Amazon Banner */}
              {isAvailableAmazon && (
                <div className="mb-8 p-6 bg-purple-500/10 border border-purple-500/30">
                  <div className="flex items-center gap-3 mb-3">
                    <span className="text-2xl">💀</span>
                    <h3 className="font-mono text-lg font-bold uppercase tracking-widest text-purple-400">
                      Availability
                    </h3>
                    <span className="text-2xl">💀</span>
                  </div>
                  <p className="text-neutral-300 text-sm">
                    Available here, or cheaper on Amazon—let's be honest, what isn't? 
                    Jeff Bezos already owns your soul; {product.title?.toLowerCase().includes('trailer hitch pin') 
                      ? "might as well let him keep your skull from going AWOL."
                      : "might as well let him power your skulls too."}
                  </p>
                </div>
              )}

              {/* Magnet Quantity Options - Hide for Coming Soon */}
              {!isComingSoon && isMiniMagnets && (
                <div className="mb-6">
                  <label className="block text-sm font-mono uppercase tracking-widest text-white mb-4">
                    Select Quantity
                  </label>
                  <div className="space-y-2">
                    {magnetOptions.map((option) => (
                      <button
                        key={option.qty}
                        data-testid={`magnet-option-${option.qty}`}
                        onClick={() => setSelectedMagnetOption(option)}
                        className={`w-full text-left px-4 py-3 border transition-all ${
                          selectedMagnetOption?.qty === option.qty
                            ? 'border-cyan-400 bg-cyan-400/10 text-white'
                            : 'border-white/10 text-neutral-400 hover:border-white/30 hover:text-white'
                        }`}
                      >
                        <span className="font-mono">{option.label}</span>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Desktop Skull Options - Mandatory selections */}
              {!isComingSoon && isDesktopSkull && (
                <div className="mb-6 space-y-4">
                  {/* Control Type Option */}
                  <div>
                    <label className="block text-sm font-mono uppercase tracking-widest text-white mb-3">
                      Control Type <span className="text-red-400">*</span>
                    </label>
                    <div className="space-y-2">
                      <button
                        data-testid="desktop-skull-control-remote"
                        onClick={() => setDesktopSkullControl('remote')}
                        className={`w-full text-left px-4 py-3 border transition-all ${
                          desktopSkullControl === 'remote'
                            ? 'border-cyan-400 bg-cyan-400/10 text-white'
                            : 'border-white/10 text-neutral-400 hover:border-white/30 hover:text-white'
                        }`}
                      >
                        <span className="font-mono">With Remote</span>
                      </button>
                      <button
                        data-testid="desktop-skull-control-app"
                        onClick={() => setDesktopSkullControl('app')}
                        className={`w-full text-left px-4 py-3 border transition-all ${
                          desktopSkullControl === 'app'
                            ? 'border-cyan-400 bg-cyan-400/10 text-white'
                            : 'border-white/10 text-neutral-400 hover:border-white/30 hover:text-white'
                        }`}
                      >
                        <span className="font-mono">App Controlled</span>
                      </button>
                    </div>
                  </div>

                  {/* Base Covering Option */}
                  <div>
                    <label className="block text-sm font-mono uppercase tracking-widest text-white mb-3">
                      Base Covering <span className="text-red-400">*</span>
                    </label>
                    <div className="space-y-2">
                      <button
                        data-testid="desktop-skull-covering-black"
                        onClick={() => setDesktopSkullCovering('black')}
                        className={`w-full text-left px-4 py-3 border transition-all ${
                          desktopSkullCovering === 'black'
                            ? 'border-cyan-400 bg-cyan-400/10 text-white'
                            : 'border-white/10 text-neutral-400 hover:border-white/30 hover:text-white'
                        }`}
                      >
                        <span className="font-mono">Black Covering</span>
                      </button>
                      <button
                        data-testid="desktop-skull-covering-newspaper"
                        onClick={() => setDesktopSkullCovering('newspaper')}
                        className={`w-full text-left px-4 py-3 border transition-all ${
                          desktopSkullCovering === 'newspaper'
                            ? 'border-cyan-400 bg-cyan-400/10 text-white'
                            : 'border-white/10 text-neutral-400 hover:border-white/30 hover:text-white'
                        }`}
                      >
                        <span className="font-mono">Vintage Newspaper</span>
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* Color Suggestion for Resin Art (excluding Mini Magnets, Solar Skulls, Trailer Hitch Skullz, Desktop Skull, and Coming Soon) */}
              {!isComingSoon && product.materials?.toLowerCase().includes('resin') && !isMiniMagnets && !isSolarSkulls && !isTrailerHitchSkullz && !isDesktopSkull && (
                <div className="mb-6">
                  <label className="block text-sm font-mono uppercase tracking-widest text-white mb-2">
                    Color Preference (Optional)
                  </label>
                  <input
                    type="text"
                    data-testid="color-preference"
                    value={colorPreference}
                    onChange={(e) => setColorPreference(e.target.value)}
                    placeholder="Suggest a color (e.g., deep purple, ocean blue, blood red...)"
                    className="w-full bg-void-paper border border-white/10 h-12 px-4 text-white placeholder:text-neutral-600 focus:border-cyan-400/50 focus:outline-none transition-colors"
                  />
                  <p className="text-xs text-neutral-500 mt-2">
                    Let us know your preferred color and we'll do our best to match it!
                  </p>
                </div>
              )}

              {/* Addon Product Option - Hide for Coming Soon */}
              {!isComingSoon && addonProduct && (
                <div className="mb-6 p-4 border border-cyan-400/30 bg-cyan-400/5 rounded">
                  <label className="flex items-start gap-4 cursor-pointer group">
                    <input
                      type="checkbox"
                      checked={includeAddon}
                      onChange={(e) => setIncludeAddon(e.target.checked)}
                      className="mt-1 w-5 h-5 accent-cyan-400"
                    />
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-1">
                        {addonProduct.images?.[0] && (
                          <div 
                            className="relative cursor-pointer group/img"
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              setShowAddonImage(true);
                            }}
                          >
                            <img 
                              src={addonProduct.images[0].url} 
                              alt={addonProduct.title}
                              className="w-12 h-12 object-cover rounded transition-transform group-hover/img:scale-105"
                            />
                            <div className="absolute inset-0 bg-black/50 opacity-0 group-hover/img:opacity-100 transition-opacity flex items-center justify-center rounded pointer-events-none">
                              <ZoomIn className="w-4 h-4 text-white" />
                            </div>
                          </div>
                        )}
                        <div>
                          <p className="text-white font-medium group-hover:text-cyan-400 transition-colors">
                            {addonProduct.title.trim().toLowerCase().startsWith('add') 
                              ? addonProduct.title.trim()
                              : `Add ${addonProduct.title.trim()}`}
                            <span className="text-cyan-400 ml-2">+${addonProduct.price?.toFixed(2)}</span>
                          </p>
                          <p className="text-neutral-500 text-sm">{addonProduct.addonDescription || 'Recommended accessory'}</p>
                        </div>
                      </div>
                    </div>
                  </label>
                </div>
              )}

              {/* Store Paused Banner */}
              {!STORE_CONFIG.STORE_OPEN && !isComingSoon && (
                <div className="mb-6 p-4 bg-amber-500/10 border border-amber-500/30 rounded">
                  <div className="flex items-center gap-3">
                    <AlertTriangle className="w-5 h-5 text-amber-500 flex-shrink-0" />
                    <p className="text-amber-400 text-sm">{STORE_CONFIG.PAUSED_MESSAGE}</p>
                  </div>
                </div>
              )}

              {/* Quantity & Add to Cart - Hide for Coming Soon */}
              {!isComingSoon && (
              <div className="flex flex-col sm:flex-row gap-4 mb-6">
                {!isMiniMagnets && STORE_CONFIG.STORE_OPEN && (
                  <div className="flex items-center border border-white/20">
                    <button
                      data-testid="decrease-quantity"
                      onClick={() => setQuantity(Math.max(1, quantity - 1))}
                      className="w-12 h-14 flex items-center justify-center text-neutral-400 hover:text-white transition-colors"
                    >
                      <Minus className="w-4 h-4" />
                    </button>
                    <span
                      data-testid="quantity-display"
                      className="w-12 h-14 flex items-center justify-center font-mono text-white"
                    >
                      {quantity}
                    </span>
                  <button
                    data-testid="increase-quantity"
                    onClick={() => setQuantity(Math.min(product.stock, quantity + 1))}
                    className="w-12 h-14 flex items-center justify-center text-neutral-400 hover:text-white transition-colors"
                  >
                    <Plus className="w-4 h-4" />
                  </button>
                </div>
                )}
                <button
                  data-testid="add-to-cart-btn"
                  onClick={STORE_CONFIG.STORE_OPEN ? handleAddToCart : () => toast.error(STORE_CONFIG.PAUSED_MESSAGE)}
                  disabled={!STORE_CONFIG.STORE_OPEN || product.stock === 0 || (isMiniMagnets && !selectedMagnetOption)}
                  className={`flex-1 h-14 px-8 font-mono uppercase tracking-widest text-sm transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed ${
                    STORE_CONFIG.STORE_OPEN 
                      ? 'bg-cyan-400 text-black hover:bg-cyan-300 hover:shadow-[0_0_20px_rgba(34,211,238,0.4)]'
                      : 'bg-neutral-700 text-neutral-400'
                  }`}
                >
                  {!STORE_CONFIG.STORE_OPEN ? STORE_CONFIG.PAUSED_BUTTON_TEXT : product.stock === 0 ? 'Sold Out' : isMiniMagnets && selectedMagnetOption ? `Add ${selectedMagnetOption.qty} Magnets - $${selectedMagnetOption.price}` : 'Add to Cart'}
                </button>
              </div>
              )}

              {/* Built-to-Order Section - Always Visible, Hide for Coming Soon and Available Amazon */}
              {!isComingSoon && !isAvailableAmazon && STORE_CONFIG.STORE_OPEN && (
              <div className="mb-8">
                {/* BTO Header */}
                <div className="w-full flex items-center gap-3 px-4 py-3 border border-amber-500/40 bg-amber-500/5">
                  <Hammer className="w-5 h-5 text-amber-500" />
                  <span className="font-mono text-sm uppercase tracking-widest text-amber-500 font-semibold">
                    Built - to - Order
                  </span>
                </div>
                
                {/* BTO Content - Always Visible */}
                <div className="px-4 py-5 border border-t-0 border-amber-500/20 bg-void-paper">
                  <h4 className="font-mono text-xs uppercase tracking-[0.2em] text-amber-500 font-bold mb-4">
                    B-T-O Acknowledgment
                  </h4>
                  <p className="text-neutral-300 text-sm leading-relaxed mb-3">
                    By engaging this purchase protocol, you hereby:
                  </p>
                  <ul className="space-y-2 mb-4">
                    <li className="flex items-start gap-2 text-neutral-400 text-sm">
                      <span className="text-amber-500 font-bold">•</span>
                      <span><span className="text-white font-semibold">COMMIT</span> full payment upfront</span>
                    </li>
                    <li className="flex items-start gap-2 text-neutral-400 text-sm">
                      <span className="text-amber-500 font-bold">•</span>
                      <span><span className="text-white font-semibold">ENLIST</span> in the production queue</span>
                    </li>
                    <li className="flex items-start gap-2 text-neutral-400 text-sm">
                      <span className="text-amber-500 font-bold">•</span>
                      <span><span className="text-white font-semibold">ACCEPT</span> that your item does not exist yet—but it will</span>
                    </li>
                  </ul>
                  <div className="pt-3 border-t border-amber-500/10 mb-5">
                    <p className="text-neutral-500 text-xs leading-relaxed mb-2">
                      No extraction requests. No timeline guarantees.
                    </p>
                    <p className="text-neutral-400 text-sm font-medium">
                      You wait. You receive. You conquer.
                    </p>
                    <p className="text-amber-500/80 text-xs uppercase tracking-wider mt-3 font-mono">
                      This is not retail. This is a build operation.
                    </p>
                    <p className="text-neutral-600 text-xs mt-2 italic">
                      AWOL requests denied. Whining court-martialed.
                    </p>
                  </div>
                  
                  {/* BTO Purchase Button */}
                  <button
                    data-testid="bto-purchase-btn"
                    onClick={handleOpenBTOModal}
                    className="w-full bg-amber-500 text-black h-12 px-6 font-mono uppercase tracking-widest text-sm font-bold hover:bg-amber-400 transition-all duration-300 hover:shadow-[0_0_20px_rgba(245,158,11,0.4)] flex items-center justify-center gap-2"
                  >
                    <Hammer className="w-4 h-4" />
                    Order Built-to-Order - ${product.price.toFixed(2)}
                  </button>
                </div>
              </div>
              )}

              {/* BTO Checkout Modal */}
              {showBTOModal && (
                <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
                  <div className="bg-void-paper border border-amber-500/30 w-full max-w-md max-h-[90vh] overflow-y-auto">
                    {/* Modal Header */}
                    <div className="flex items-center justify-between p-4 border-b border-amber-500/20">
                      <div className="flex items-center gap-3">
                        <Hammer className="w-5 h-5 text-amber-500" />
                        <h3 className="font-mono text-sm uppercase tracking-widest text-amber-500 font-bold">
                          Built-to-Order
                        </h3>
                      </div>
                      <button
                        onClick={() => setShowBTOModal(false)}
                        className="text-neutral-400 hover:text-white transition-colors"
                      >
                        <X className="w-5 h-5" />
                      </button>
                    </div>

                    {/* Product Summary */}
                    <div className="p-4 border-b border-white/10 bg-amber-500/5">
                      <div className="flex gap-4">
                        <img
                          src={product.images[0]?.url}
                          alt={product.title}
                          className="w-16 h-16 object-cover"
                        />
                        <div className="flex-1">
                          <p className="text-white font-medium text-sm">{product.title}</p>
                          <p className="text-amber-500 font-mono">${product.price.toFixed(2)}</p>
                        </div>
                      </div>
                    </div>

                    {/* BTO Options */}
                    <div className="p-4 space-y-4">
                      {/* BTO Info Notice */}
                      <div className="p-4 bg-amber-500/10 border border-amber-500/30 rounded">
                        <p className="text-amber-400 text-sm leading-relaxed">
                          Your order will be placed in line to be made and you will receive an email shortly from us with updates on your order status.
                        </p>
                      </div>

                      {/* Quantity */}
                      <div>
                        <label className="block text-xs text-neutral-400 uppercase tracking-wider mb-1">Quantity</label>
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => setBtoQuantity(Math.max(1, btoQuantity - 1))}
                            className="w-10 h-10 border border-white/20 text-white hover:bg-white/10"
                          >
                            <Minus className="w-4 h-4 mx-auto" />
                          </button>
                          <span className="w-12 text-center font-mono text-white">{btoQuantity}</span>
                          <button
                            onClick={() => setBtoQuantity(btoQuantity + 1)}
                            className="w-10 h-10 border border-white/20 text-white hover:bg-white/10"
                          >
                            <Plus className="w-4 h-4 mx-auto" />
                          </button>
                        </div>
                      </div>

                      {/* Desktop Skull BTO Options */}
                      {isDesktopSkull && (
                        <>
                          {/* Control Type Option */}
                          <div>
                            <label className="block text-xs text-neutral-400 uppercase tracking-wider mb-2">
                              Control Type <span className="text-red-400">*</span>
                            </label>
                            <div className="space-y-2">
                              <button
                                onClick={() => setBtoDesktopSkullControl('remote')}
                                className={`w-full text-left px-3 py-2 border text-sm transition-all ${
                                  btoDesktopSkullControl === 'remote'
                                    ? 'border-amber-500 bg-amber-500/10 text-white'
                                    : 'border-white/10 text-neutral-400 hover:border-white/30 hover:text-white'
                                }`}
                              >
                                <span className="font-mono">With Remote</span>
                              </button>
                              <button
                                onClick={() => setBtoDesktopSkullControl('app')}
                                className={`w-full text-left px-3 py-2 border text-sm transition-all ${
                                  btoDesktopSkullControl === 'app'
                                    ? 'border-amber-500 bg-amber-500/10 text-white'
                                    : 'border-white/10 text-neutral-400 hover:border-white/30 hover:text-white'
                                }`}
                              >
                                <span className="font-mono">App Controlled</span>
                              </button>
                            </div>
                          </div>

                          {/* Base Covering Option */}
                          <div>
                            <label className="block text-xs text-neutral-400 uppercase tracking-wider mb-2">
                              Base Covering <span className="text-red-400">*</span>
                            </label>
                            <div className="space-y-2">
                              <button
                                onClick={() => setBtoDesktopSkullCovering('black')}
                                className={`w-full text-left px-3 py-2 border text-sm transition-all ${
                                  btoDesktopSkullCovering === 'black'
                                    ? 'border-amber-500 bg-amber-500/10 text-white'
                                    : 'border-white/10 text-neutral-400 hover:border-white/30 hover:text-white'
                                }`}
                              >
                                <span className="font-mono">Black Covering</span>
                              </button>
                              <button
                                onClick={() => setBtoDesktopSkullCovering('newspaper')}
                                className={`w-full text-left px-3 py-2 border text-sm transition-all ${
                                  btoDesktopSkullCovering === 'newspaper'
                                    ? 'border-amber-500 bg-amber-500/10 text-white'
                                    : 'border-white/10 text-neutral-400 hover:border-white/30 hover:text-white'
                                }`}
                              >
                                <span className="font-mono">Vintage Newspaper</span>
                              </button>
                            </div>
                          </div>
                        </>
                      )}

                      {/* Addon Option (if available) */}
                      {addonProduct && (
                        <div className="p-3 border border-cyan-400/30 bg-cyan-400/5">
                          <label className="flex items-start gap-3 cursor-pointer group">
                            <input
                              type="checkbox"
                              checked={btoIncludeAddon}
                              onChange={(e) => setBtoIncludeAddon(e.target.checked)}
                              className="mt-1 w-4 h-4 accent-cyan-400"
                            />
                            <div className="flex-1">
                              <div className="flex items-center gap-2">
                                {addonProduct.images?.[0] && (
                                  <img 
                                    src={addonProduct.images[0].url} 
                                    alt={addonProduct.title}
                                    className="w-10 h-10 object-cover rounded"
                                  />
                                )}
                                <div>
                                  <p className="text-white text-sm font-medium group-hover:text-cyan-400 transition-colors">
                                    {addonProduct.title.trim().toLowerCase().startsWith('add') 
                                      ? addonProduct.title.trim()
                                      : `Add ${addonProduct.title.trim()}`}
                                    <span className="text-cyan-400 ml-2">+${addonProduct.price?.toFixed(2)}</span>
                                  </p>
                                  <p className="text-neutral-500 text-xs">{addonProduct.addonDescription || 'Recommended accessory'}</p>
                                </div>
                              </div>
                            </div>
                          </label>
                        </div>
                      )}

                      <hr className="border-white/10" />

                      {/* Order Summary */}
                      <div className="bg-void p-4 border border-white/10">
                        <h5 className="font-mono text-xs uppercase tracking-widest text-amber-500 mb-3">Order Summary</h5>
                        <div className="space-y-2 text-sm">
                          <div className="flex justify-between text-neutral-400">
                            <span>[BTO] {product.title} x {btoQuantity}</span>
                            <span>${(product.price * btoQuantity).toFixed(2)}</span>
                          </div>
                          {btoIncludeAddon && addonProduct && (
                            <div className="flex justify-between text-cyan-400">
                              <span>+ {addonProduct.title.trim()}</span>
                              <span>${addonProduct.price.toFixed(2)}</span>
                            </div>
                          )}
                          <hr className="border-white/10" />
                          <div className="flex justify-between text-white font-bold">
                            <span>Subtotal</span>
                            <span className="text-amber-500">${getBTOSubtotal().toFixed(2)}</span>
                          </div>
                          <p className="text-neutral-500 text-xs">Shipping calculated at checkout</p>
                        </div>
                      </div>

                      <button
                        onClick={handleAddBTOToCart}
                        className="w-full bg-amber-500 text-black h-12 font-mono uppercase tracking-widest text-sm font-bold hover:bg-amber-400 transition-all flex items-center justify-center gap-2"
                      >
                        <Hammer className="w-4 h-4" />
                        Add BTO to Cart - ${getBTOSubtotal().toFixed(2)}
                      </button>

                      <p className="text-neutral-500 text-xs text-center">
                        BTO items will be marked in your cart. Complete checkout to place your order.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Add-on Image Modal */}
              {showAddonImage && addonProduct && (
                <div 
                  data-testid="addon-image-modal"
                  className="fixed inset-0 bg-black/90 backdrop-blur-sm z-50 flex items-center justify-center p-4"
                  onClick={() => setShowAddonImage(false)}
                >
                  <div className="relative max-w-2xl w-full">
                    <button
                      data-testid="close-addon-modal"
                      onClick={() => setShowAddonImage(false)}
                      className="absolute -top-12 right-0 text-white hover:text-cyan-400 transition-colors"
                    >
                      <X className="w-8 h-8" />
                    </button>
                    <div className="bg-void-paper border border-cyan-400/30 p-2">
                      <img 
                        src={addonProduct.images?.[0]?.url} 
                        alt={addonProduct.title}
                        data-testid="addon-modal-image"
                        className="w-full h-auto object-contain max-h-[70vh]"
                      />
                      <div className="p-4 border-t border-white/10">
                        <h3 className="text-white font-medium text-lg">{addonProduct.title}</h3>
                        <p className="text-cyan-400 font-mono">${addonProduct.price?.toFixed(2)}</p>
                        {addonProduct.description && (
                          <p className="text-neutral-400 text-sm mt-2">{addonProduct.description}</p>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Product Image Modal */}
              {showProductImageModal && product && (
                <div 
                  data-testid="product-image-modal"
                  className="fixed inset-0 bg-black/95 backdrop-blur-sm z-50 flex items-center justify-center p-4"
                  onClick={() => setShowProductImageModal(false)}
                >
                  <div className="relative max-w-4xl w-full" onClick={(e) => e.stopPropagation()}>
                    {/* Close button */}
                    <button
                      data-testid="close-product-image-modal"
                      onClick={() => setShowProductImageModal(false)}
                      className="absolute -top-12 right-0 text-white hover:text-cyan-400 transition-colors z-10"
                    >
                      <X className="w-8 h-8" />
                    </button>
                    
                    {/* Main zoomed image */}
                    <div className="bg-void-paper border border-cyan-400/30">
                      <img 
                        src={product.images[selectedImage]?.url} 
                        alt={product.images[selectedImage]?.alt || product.title}
                        data-testid="product-modal-image"
                        className="w-full h-auto object-contain max-h-[75vh]"
                      />
                    </div>
                    
                    {/* Thumbnail navigation in modal */}
                    {product.images.length > 1 && (
                      <div className="flex justify-center gap-3 mt-4">
                        {product.images.map((image, index) => (
                          <button
                            key={image.url || `modal-thumb-${index}`}
                            onClick={() => setSelectedImage(index)}
                            className={`w-16 h-16 overflow-hidden border-2 transition-all ${
                              selectedImage === index
                                ? 'border-cyan-400 scale-110'
                                : 'border-white/20 hover:border-white/50 opacity-60 hover:opacity-100'
                            }`}
                          >
                            <img
                              src={image.url}
                              alt={image.alt}
                              className="w-full h-full object-cover"
                            />
                          </button>
                        ))}
                      </div>
                    )}
                    
                    {/* Product title */}
                    <p className="text-center text-white/70 mt-4 font-mono text-sm">
                      {product.title}
                    </p>
                  </div>
                </div>
              )}

              {/* Product Details Accordion */}
              <Accordion type="single" collapsible defaultValue="dimensions" className="border-t border-white/10">
                <AccordionItem value="dimensions" className="border-white/10">
                  <AccordionTrigger
                    data-testid="dimensions-accordion"
                    className="font-mono text-sm uppercase tracking-widest text-white hover:text-cyan-400"
                  >
                    <span className="flex items-center gap-3">
                      <Ruler className="w-4 h-4" /> Dimensions
                    </span>
                  </AccordionTrigger>
                  <AccordionContent className="text-neutral-400">
                    {product.dimensions}
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem value="materials" className="border-white/10">
                  <AccordionTrigger
                    data-testid="materials-accordion"
                    className="font-mono text-sm uppercase tracking-widest text-white hover:text-cyan-400"
                  >
                    <span className="flex items-center gap-3">
                      <Droplets className="w-4 h-4" /> Materials
                    </span>
                  </AccordionTrigger>
                  <AccordionContent className="text-neutral-400">
                    {product.materials}
                  </AccordionContent>
                </AccordionItem>

                {/* Weight - only for Metal Art (Steel products) */}
                {product.materials?.toLowerCase().includes('steel') && (
                  <AccordionItem value="weight" className="border-white/10">
                    <AccordionTrigger
                      data-testid="weight-accordion"
                      className="font-mono text-sm uppercase tracking-widest text-white hover:text-cyan-400"
                    >
                      <span className="flex items-center gap-3">
                        <Weight className="w-4 h-4" /> Weight
                      </span>
                    </AccordionTrigger>
                    <AccordionContent className="text-neutral-400">
                      2 lbs
                    </AccordionContent>
                  </AccordionItem>
                )}

                <AccordionItem value="care" className="border-white/10">
                  <AccordionTrigger
                    data-testid="care-accordion"
                    className="font-mono text-sm uppercase tracking-widest text-white hover:text-cyan-400"
                  >
                    <span className="flex items-center gap-3">
                      {product.care_instructions?.startsWith('INTENDED USE') ? (
                        <><Target className="w-4 h-4" /> Intended Use</>
                      ) : (
                        <><Shield className="w-4 h-4" /> Care Instructions</>
                      )}
                    </span>
                  </AccordionTrigger>
                  <AccordionContent className="text-neutral-400">
                    {(() => {
                      let instructions = product.care_instructions || '';
                      // Remove "INTENDED USE:" prefix if present
                      if (instructions.startsWith('INTENDED USE:')) {
                        instructions = instructions.replace('INTENDED USE:', '').trim();
                      }
                      // Split by pipe, newlines, or bullet characters and filter empty items
                      const items = instructions
                        .split(/[|\n•]/)
                        .map(item => item.trim())
                        .filter(item => item.length > 0);
                      
                      if (items.length > 1) {
                        return (
                          <ul className="list-disc list-inside space-y-1">
                            {items.map((item, idx) => (
                              <li key={`care-${idx}-${item.slice(0, 10)}`}>{item}</li>
                            ))}
                          </ul>
                        );
                      }
                      return instructions;
                    })()}
                  </AccordionContent>
                </AccordionItem>

                {/* Legal Disclaimer - Only for Solar Skulls */}
                {isSolarSkulls && (
                  <AccordionItem value="legal" className="border-white/10">
                    <AccordionTrigger
                      data-testid="legal-accordion"
                      className="font-mono text-sm uppercase tracking-widest text-white hover:text-cyan-400"
                    >
                      <span className="flex items-center gap-3">
                        <ScrollText className="w-4 h-4" /> Legal Notice
                      </span>
                    </AccordionTrigger>
                    <AccordionContent className="text-neutral-400">
                      <span className="text-cyan-400 font-semibold">Notice:</span> This product incorporates lawfully acquired, up-cycled materials that have been resold and modified in accordance with applicable laws, including the First Sale Doctrine. All customizations and modifications are independently performed by Only Skullz LLC and are not authorized, sponsored, or endorsed by the original manufacturer. No affiliation with the original brand is implied.
                    </AccordionContent>
                  </AccordionItem>
                )}

                <AccordionItem value="shipping" className="border-white/10">
                  <AccordionTrigger
                    data-testid="shipping-accordion"
                    className="font-mono text-sm uppercase tracking-widest text-white hover:text-cyan-400"
                  >
                    <span className="flex items-center gap-3">
                      <Package className="w-4 h-4" /> Shipping
                    </span>
                  </AccordionTrigger>
                  <AccordionContent className="text-neutral-400">
                    Only Skullz LLC uses Shippo for all shipping to deliver fast, reliable orders at the best possible rates. It allows us to choose the best carrier every time and provide clear pricing and real-time tracking—so our clients get dependable delivery without the guesswork.
                  </AccordionContent>
                </AccordionItem>
              </Accordion>
            </div>
          </div>

          {/* Related Products */}
          {relatedProducts.length > 0 && (
            <div className="mt-24 pt-16 border-t border-white/5">
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-white mb-8">
                You May Also Like
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                {relatedProducts.map((product) => (
                  <ProductCard key={product.id} product={product} />
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProductDetailPage;
